// Wizard step indicator. Pure-HTML helper (no Component class) so it can be
// embedded directly in template() output the same way StatusBadge is. The
// caller passes an ordered array of {key,label} entries plus the 1-based
// `current` index. Past steps render as completed (filled circle), the active
// one as primary, future ones as muted.

import { esc } from '../core/component.js';
import { icon } from '../core/icons.js';

/**
 * @param {Array<{key:string,label:string}>} steps
 * @param {number} current  1-based index of the active step
 * @param {{onClickable?: boolean}} [opts]  When `onClickable`, past steps are
 *                                          rendered as buttons (data-step-jump).
 */
export function stepper(steps, current, opts = {}) {
  const total = steps.length;
  const safeCurrent = Math.min(Math.max(1, Number(current) || 1), total);
  const pct = total <= 1 ? 100 : Math.round(((safeCurrent - 1) / (total - 1)) * 100);
  const items = steps.map((step, i) => {
    const idx = i + 1;
    const state = idx < safeCurrent ? 'done' : idx === safeCurrent ? 'active' : 'todo';
    // Bullet shows the v5 §N section-sign prefix when the step is pending or
    // active; completed steps render a checkmark so the user can see at a
    // glance which segments are already behind them.
    const inner = state === 'done'
      ? icon('check', { size: 'sm' })
      : `<span>§${idx}</span>`;
    const tag = opts.onClickable && state === 'done' ? 'button' : 'div';
    const attrs = tag === 'button'
      ? `type="button" data-step-jump="${idx}"`
      : '';
    return `
      <${tag} class="stepper-item stepper-${esc(state)}" ${attrs}>
        <span class="stepper-bullet">${inner}</span>
        <span class="stepper-label">${esc(step.label)}</span>
      </${tag}>
    `;
  }).join('');
  return `
    <div class="stepper" role="progressbar" aria-valuemin="1" aria-valuemax="${total}" aria-valuenow="${safeCurrent}">
      <div class="stepper-track"><div class="stepper-fill" style="width:${pct}%"></div></div>
      <div class="stepper-items">${items}</div>
    </div>
  `;
}
